import { useMemo, useState } from 'react';

export type SortDir = 'asc' | 'desc';

export interface TableSortState<K extends string> {
  sortKey: K;
  dir: SortDir;
  /** Cycles: not-active → asc → desc → asc … on the same column.
   *  Switches to a new column always starts at asc (most common intent).
   *  Takes a plain string (not K) so SortableTh — which doesn't know the
   *  column-key union — can call it without type gymnastics. Unknown keys
   *  are ignored. */
  setSort: (key: string) => void;
}

/**
 * Client-side table sort helper. Callers pass a `columns` record mapping
 * each column key to a value accessor. The hook returns the sorted rows
 * plus the state object used by <SortableTh>.
 *
 * Numbers, strings, nulls, and booleans are compared natively. Nulls sort
 * last regardless of direction (consistent with the existing NULLS LAST
 * pattern in ORP's SQL).
 */
export function useTableSort<T, C extends Record<string, (row: T) => unknown>>(
  rows: T[],
  columns: C,
  defaults: { key: keyof C & string; dir?: SortDir },
): { sorted: T[]; sort: TableSortState<keyof C & string> } {
  type K = keyof C & string;
  const [sortKey, setSortKey] = useState<K>(defaults.key);
  const [dir, setDir] = useState<SortDir>(defaults.dir ?? 'asc');

  const setSort = (key: string) => {
    if (!(key in columns)) return;
    if (key === sortKey) {
      setDir(dir === 'asc' ? 'desc' : 'asc');
    } else {
      setSortKey(key as K);
      setDir('asc');
    }
  };

  const sorted = useMemo(() => {
    const get = columns[sortKey];
    if (!get) return rows;
    const factor = dir === 'asc' ? 1 : -1;
    const copy = [...rows];
    copy.sort((a, b) => compareValues(get(a), get(b)) * factor);
    return copy;
  }, [rows, sortKey, dir, columns]);

  return { sorted, sort: { sortKey, dir, setSort } };
}

function compareValues(a: unknown, b: unknown): number {
  // Nulls/undefined sort last regardless of direction.
  const aNull = a == null || a === '';
  const bNull = b == null || b === '';
  if (aNull && bNull) return 0;
  if (aNull) return 1;
  if (bNull) return -1;
  if (typeof a === 'number' && typeof b === 'number') return a - b;
  if (typeof a === 'boolean' && typeof b === 'boolean') return (a ? 1 : 0) - (b ? 1 : 0);
  // Treat everything else as string (dates in ISO are naturally string-sortable).
  return String(a).localeCompare(String(b), undefined, { numeric: true, sensitivity: 'base' });
}
